import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { min } from '@adornis/validation/functions/range.js';
import { DateTime } from 'luxon';
import { Product } from './products.js';
import { User } from './user.js';

@Entity()
export class Teaching extends MongoEntity {
  static override _class = 'Teaching';
  static override _collectionName = 'teachings';

  @validate(nonOptional())
  @Field(type => ID)
  lecturerID!: string;

  @DataLoadedField(type => User, teaching => ({ _id: teaching.lecturerID }))
  lecturer: Maybe<User>;

  @validate(nonOptional())
  @Field(type => ID)
  productID!: string;

  @DataLoadedField(type => Product, teaching => ({ _id: teaching.productID }))
  product: Maybe<Product>;

  @Field(type => DateTime, { default: v => v ?? DateTime.now() })
  createdAt!: DateTime;

  @Field(type => ID)
  contractID: Maybe<string>;

  @validate(min(0))
  @Field(type => Float)
  contractPrice: Maybe<number>;

  @Field(type => String)
  contractPriceWords: Maybe<string>;

  @Field(type => ID)
  signedContractFileID: Maybe<string>;

  override toString() {
    return `Lecturer: ${this.lecturerID} -> Product: ${this.productID}`;
  }
}

export const subscribeTeachingsByUserId = registerSubscription({
  operationName: 'subscribeTeachingsByUserId',
  type: () => [Teaching],
  params: [{ name: 'userID', type: () => String }],
  resolve: (userID: string) => {
    return (gqlFields: BaseQLSelectionSet<Teaching>) => {
      return watchQuery(Teaching._collectionName, {
        lecturerID: userID,
      });
    };
  },
});

export const getTeachingsByLecturerID = registerQuery({
  operationName: 'getTeachingsByLecturerID',
  resolve: (lecturerID: string) => async (gqlFields: BaseQLSelectionSet<Teaching>) => {
    const teachingsForLecturer = await (await getRawCollection<Teaching>(Teaching._collectionName))
      .find({ lecturerID })
      .toArray();

    return teachingsForLecturer;
  },
  type: () => [Teaching],
  params: [{ name: 'lecturerID', type: () => String }],
});

export const getLecturerProductTeaching = registerQuery({
  operationName: 'getLecturerProductTeaching',
  resolve: (lecturerID: string, productID: string) => async (gqlFields: BaseQLSelectionSet<Teaching>) => {
    const teachingsForLecturer = await (await getRawCollection<Teaching>(Teaching._collectionName))
      .find({ lecturerID, productID })
      .toArray();

    return teachingsForLecturer;
  },
  type: () => [Teaching],
  params: [
    { name: 'lecturerID', type: () => String },
    { name: 'productID', type: () => String },
  ],
});
